import React, { useState, useEffect, useCallback, useRef } from "react";
import logo from "./logo.svg";
import styled from "styled-components/macro";
import Axios from "axios";
import Theme from "theme";
import { DefaultGlobals, LayoutList } from "components";
import { Box, QdayAnimation, NewSidebar } from "q-components";
import { updateUrl, charFixer } from "./helpers";
import { blink } from "./q-components/QdayAnimation";

export const IconRight = (
  <svg
    width="20"
    height="13"
    viewBox="0 0 20 13"
    fill="none"
    xmlns="http://www.w3.org/2000/svg"
  >
    <path
      d="M14.125 12.0632C13.875 11.8105 13.875 11.5579 14.125 11.3053L18.3125 6.63158H0.5C0.166665 6.63158 0 6.46316 0 6.12632C0 5.78947 0.166665 5.62105 0.5 5.62105H18.3125L14.125 0.947369C13.875 0.694736 13.875 0.442107 14.125 0.189475C14.375 -0.0631582 14.625 -0.0631582 14.875 0.189475C18.1667 3.85265 19.8333 5.70526 19.875 5.74737C19.9583 5.83158 20 5.95789 20 6.12632C20 6.29474 19.9583 6.42105 19.875 6.50526L14.875 12.0632C14.7917 12.1474 14.6667 12.1895 14.5 12.1895C14.3333 12.1895 14.2083 12.1474 14.125 12.0632Z"
      fill="currentColor"
    />
  </svg>
);

const resetMeta = () => {
  document.title = "Qday - All Digital";
  document
    .querySelector('meta[name="description"]')
    .setAttribute(
      "content",
      "This year we decided to go all digital, and by doing so, we provide you to have the option to view ALL speakers in your own time."
    );
};

const getSlug = () => {
  let params = new URL(document.location).searchParams;
  let p = params.get("p");
  return p;
};

const Vignette = styled.div`
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  pointer-events: none;
  z-index: 2;

  &::before,
  &::after {
    content: "";
    position: absolute;
    top: 0;
    width: 40vw;
    height: 40vw;
  }
  &::before {
    left: 0;
    top: 0;
    transform-origin: top left;
    background: linear-gradient(
      135deg,
      rgba(24, 24, 24, 0.5) 0%,
      rgba(24, 24, 24, 0) 50%
    );
  }
  &::after {
    right: 0;
    top: 0;
    transform-origin: top right;
    background: linear-gradient(
      -135deg,
      rgba(24, 24, 24, 0.5) 0%,
      rgba(24, 24, 24, 0) 50%
    );
  }
`;

const Header = styled.header`
  display: flex;
  flex-direction: column;
  justify-content: space-between;
  min-height: 100vh; /* TODO: Fix for mobile scrolling */
  text-align: center;
  padding: var(--s-medium);
  .clarification {
    text-transform: uppercase;
  }
  .logo {
    width: 750px;
    position: relative;
    z-index: 1;
    animation: ${blink} 1s forwards;
    filter: drop-shadow(0 0 5px rgba(255, 255, 255, 0.6));
    transition: filter var(--t-slow) var(--e-out);
    &:hover {
      filter: drop-shadow(0 0 10px rgba(255, 255, 255, 0.5));
    }
  }

  .logoText {
    font-size: 2.5rem;
    margin-top: 40px;
    margin-bottom: 40px;
    position: relative;
    z-index: 1;
    animation: ${blink} 1s forwards;
    filter: drop-shadow(0 0 5px rgba(255, 255, 255, 0.6));
    transition: filter var(--t-slow) var(--e-out);
    &:hover {
      filter: drop-shadow(0 0 10px rgba(255, 255, 255, 0.5));
    }
  }
  .ingress {
    color: var(--c-text);
    filter: drop-shadow(0 0 5px rgba(255, 255, 255, 0.6));
    margin: auto;
    max-width: 93%;
    @media (min-width: 1230px) {
      max-width: 65%;
    }
  }
`;
const Content = styled.div`
  margin-left: auto;
  margin-right: auto;
  margin-top: auto;
  margin-bottom: auto;
`;

const Wrapper = styled.main`
  padding: 0 20px;
  @media (min-width: 800px) {
    padding: 0 65px;
  }
`;
const InnerWrapper = styled.main`
  h1 {
    text-align: center;
  }
  @media (min-width: 800px) {
    max-width: 870px;
    margin-left: auto;
    margin-right: auto;
    h1 {
      text-align: left;
    }
  }
`;

const TimeEntryStyles = styled.section`
  .time {
    font-family: var(--f-header);
    margin-bottom: var(--s-tiny);
    transition: opacity var(--t-slow) linear;
    opacity: ${(props) => (props.active ? 1 : 0.4)};
  }

  .canceled-title {
    margin-bottom: 0;
  }

  .canceled {
    margin-top: 0;
    margin-bottom: var(--s-small);
    font-size: 16px;
    font-family: var(--f-primary);
  }
`;

const TimeEntry = (props) => {
  return <TimeEntryStyles {...props} />;
};

const App = () => {
  const [sidebarContent, setSidebarContent] = useState(false);
  const [sidebarOpen, setSidebarOpen] = useState(
    getSlug() ? "?talk=" + getSlug() : false
  );
  const [sidebarProps, setSidebarProps] = useState({});
  const isMounted = useRef(null);

  useEffect(() => {
    const handleEsc = (event) => {
      if (event.keyCode === 27) {
        setSidebarOpen(false);
        updateUrl(false);
        resetMeta();
      }
    };

    window.addEventListener("keydown", handleEsc);

    return () => {
      window.removeEventListener("keydown", handleEsc);
    };
  }, []);

  const [talks, setTalks] = useState([]);
  const [loadingTalks, setLoadingTalks] = useState(false);
  const [options, setOptions] = useState([]);
  const [currentTalk, setCurrentTalk] = useState([]);

  const today = new Date();

  const fetchTalks = async () => {
    setLoadingTalks(true);
    const talksUrl = `https://qdaycms.fullystage.se/wp-json/wp/v2/talks_2024&per_page=100`;
    Axios.get(talksUrl).then((response) => {
      try {
        if (response.data && response.data.length > 0) {
          setTalks(response.data);
        }
        setLoadingTalks(false);
      } catch (error) {
        console.error(error);
      }
    });
  };

  const fetchOptions = async () => {
    const optionsUrl = `https://qdaycms.fullystage.se/wp-json/acf/v3/options/options`;
    Axios.get(optionsUrl).then((response) => {
      try {
        if (response.data) {
          setOptions(response.data.acf);
        }
      } catch (error) {
        console.error(error);
      }
    });
  };

  const getCurrentTalk = async () => {
    const currentSlug = window.location.search.slice(6);
    if (currentSlug) {
      const tempArr = talks.filter((item) => {
        setSidebarOpen(item.slug);
        return item.slug === currentSlug;
      });

      setCurrentTalk(tempArr[0]);
      if (tempArr[0]) {
        setSidebarOpen(tempArr[0].slug);
        document.title = charFixer(tempArr[0].title.rendered + " - Qday");
        document
          .querySelector('meta[name="description"]')
          .setAttribute("content", tempArr[0].acf.talk_description);
      }
    }
  };

  useEffect(() => {
    fetchOptions();
    fetchTalks();
    isMounted.current = true;
  }, []);

  useEffect(() => {
    getCurrentTalk();
  }, [talks]);

  useEffect(() => {
    if (sidebarOpen && isMounted.current) {
      updateUrl(sidebarOpen);
    }
  }, [sidebarOpen]);

  const handlePopstate = useCallback((e) => {
    // Close modal when using back button
    if (e.state === null && !getSlug()) {
      setSidebarOpen(false);
      resetMeta();
    }
  }, []);

  const popstate = useRef(null);

  useEffect(() => {
    if (!popstate.current) {
      window.addEventListener("popstate", handlePopstate);
    }
    return () => {
      window.removeEventListener("popstate", handlePopstate);
    };
  }, [handlePopstate]);

  let timeStamp = [];

  talks.filter(
    (talk) =>
      talk.acf.time &&
      !timeStamp.includes(talk.acf.time) &&
      timeStamp.push(talk.acf.time)
  );

  timeStamp = timeStamp.sort();

  let canceled = [];

  return (
    <>
      <Vignette />
      <Theme />
      <DefaultGlobals />
      <Header>
        <QdayAnimation />
        <Content>
          <img src={logo} className="logo" alt="Qday" />
          <p className="logoText">Rome | 2024.12.07</p>
          <p className="ingress">
            Qday started in 2019 and was initially intended as a competence
            conference with a lot of tech focus. Over time, it evolved into a
            more inspiring and influential arena, surviving Covid and becoming a
            fully internal event last year. This year Qday comes with a twist,
            Qday day becomes Q-talks! The goal is to deliver inspiration,
            concise and impactful sessions, and a mix of engaging presentations.
            We’re excited to combine a strong keynote speaker with contributions
            from our internal stars. We hope you will like it!
          </p>
        </Content>
      </Header>
      <NewSidebar
        color={sidebarProps.bg}
        open={sidebarOpen}
        toggleOpen={() => {
          if (sidebarOpen) {
            updateUrl(false);
            resetMeta();
          }
          setSidebarOpen(!sidebarOpen);
        }}
        closeIcon={
          <span style={{ transform: "rotate(180deg)" }}>{IconRight}</span>
        }
        buttonProps={{
          color: "transparent",
          style: {
            marginLeft: "-30px",
            top: 0,
            overflow: "visible",
            marginBottom: 5,
            paddingTop: 0,
          },
          small: false,
        }}
        options={options}
        currentTalk={currentTalk}
        type={currentTalk && currentTalk.acf && currentTalk.acf.type}
        presentationText={options.video_presentation_text}
        talkTitle={
          currentTalk &&
          currentTalk.acf &&
          charFixer(currentTalk.title.rendered)
        }
        slug={currentTalk && currentTalk.acf && currentTalk.acf.slug}
        videoId={currentTalk && currentTalk.acf && currentTalk.acf.vimeo_id}
        videoPlaceholderImage={
          currentTalk &&
          currentTalk.acf &&
          currentTalk.acf.video_placeholder_image &&
          currentTalk.acf.video_placeholder_image.sizes["wide-1250"]
        }
        videoPlaceholderImageWidth={
          currentTalk &&
          currentTalk.acf &&
          currentTalk.acf.video_placeholder_image &&
          currentTalk.acf.video_placeholder_image.sizes["wide-1250-width"]
        }
        videoPlaceholderImageHeight={
          currentTalk &&
          currentTalk.acf &&
          currentTalk.acf.video_placeholder_image &&
          currentTalk.acf.video_placeholder_image.sizes["wide-1250-height"]
        }
        time={currentTalk && currentTalk.acf && currentTalk.acf.time}
        talkDescription={
          currentTalk && currentTalk.acf && currentTalk.acf.talk_description
        }
        speakers={currentTalk && currentTalk.acf && currentTalk.acf.speaker}
        speakerDescription={
          currentTalk && currentTalk.acf && currentTalk.acf.speaker_description
        }
        location={
          currentTalk &&
          currentTalk.acf &&
          currentTalk.acf.location &&
          currentTalk.acf.location.name
        }
        category={
          currentTalk &&
          currentTalk.acf &&
          currentTalk.acf.category &&
          currentTalk.acf.category.name
        }
        typeformLink={
          currentTalk && currentTalk.acf && currentTalk.acf.typeform_link
        }
        typeformLinkText={options.typeform_button_text}
        linkedIn={options.contact_linkedin_icon}
        facebook={options.contact_facebook_icon}
        twitter={options.contact_twitter_icon}
        shareOnSocialMedia={options.share_on_social_media}
      />
      <Wrapper>
        <InnerWrapper>
          <LayoutList>
            <h1>Program</h1>
            {timeStamp.map((time) => {
              const talksThisTime = talks.filter(
                (talk) => talk.acf.time === time
              );
              return (
                <TimeEntry active={true} key={time}>
                  <h4 className="time">{time}</h4>
                  {talksThisTime &&
                    talksThisTime.map((talk, index) => {
                      const entry = talk.acf;
                      if (talk.acf.canceled) {
                        canceled.push(talk);

                        return null;
                      }
                      const timeIndex = timeStamp.indexOf(entry.time); // Match entry.time to timeStamp
                      const colors = [
                        "--c-white",
                        "--c-neutral--500",
                        "--c-neutral--700",
                      ];
                      const bgColor =
                        entry.location.name && timeIndex >= 0
                          ? colors[timeIndex % colors.length]
                          : "--transparent"; // Assign color or default

                      return (
                        <Box
                          bgColor={bgColor}
                          active={true}
                          setCurrentTalk={setCurrentTalk}
                          key={"t" + index + entry.title}
                          talk={talk}
                          time={entry.time}
                          speakers={entry.speaker}
                          talkTitle={charFixer(talk.title.rendered)}
                          talkDescription={entry.talk_description}
                          videoId={entry.vimeo_id}
                          videoPlaceholderImage={
                            entry.video_placeholder_image &&
                            entry.video_placeholder_image.sizes.large
                          }
                          options={options}
                          setSidebarProps={setSidebarProps}
                          setSidebarOpen={setSidebarOpen}
                          setSidebarContent={setSidebarContent}
                          sidebarOpen={sidebarOpen}
                          slug={entry.slug}
                          type={entry.type}
                          location={entry.location}
                          category={entry.category}
                          canceled={entry.canceled}
                        />
                      );
                    })}
                </TimeEntry>
              );
            })}
            {canceled && canceled.length > 0 && (
              <TimeEntry active={true} key="canceled">
                <h4 className="time canceled-title">{options.canceled}</h4>
                <p className="canceled">{options.canceled_text}</p>
                {canceled &&
                  canceled.map((talk, index) => {
                    const entry = talk.acf;
                    return (
                      <Box
                        active={true}
                        setCurrentTalk={setCurrentTalk}
                        key={"t" + index + entry.title}
                        talk={talk}
                        time={entry.time}
                        speakers={entry.speaker}
                        talkTitle={charFixer(talk.title.rendered)}
                        talkDescription={entry.talk_description}
                        videoId={entry.vimeo_id}
                        videoPlaceholderImage={
                          entry.video_placeholder_image &&
                          entry.video_placeholder_image.sizes.large
                        }
                        options={options}
                        setSidebarProps={setSidebarProps}
                        setSidebarOpen={setSidebarOpen}
                        setSidebarContent={setSidebarContent}
                        sidebarOpen={sidebarOpen}
                        slug={entry.slug}
                        type={entry.type}
                        location={entry.location}
                        category={entry.category}
                        canceled={entry.canceled}
                      />
                    );
                  })}
              </TimeEntry>
            )}
          </LayoutList>
        </InnerWrapper>
      </Wrapper>
    </>
  );
};

export default App;
